import { terminusPhrase } from './chunk-BDW6HA43.js';
import { protocolDisplayName } from './chunk-IHYVRZAP.js';
import { truncateMiddle, hexPrefixOf, shortenAddress, normalizeTxHash } from './chunk-35LGZV5V.js';
import { chainDisplayName } from './chunk-SWMPV6WD.js';

// src/grouping/roles.generated.json
var roles_generated_default = {
  roles: [
    {
      name: "source",
      side: "origin",
      complement: "destination",
      synonyms: [
        "send",
        "origin"
      ],
      rank: 0
    },
    {
      name: "destination",
      side: "destination",
      complement: "source",
      synonyms: [
        "receive",
        "dest"
      ],
      rank: 1
    },
    {
      name: "accepted",
      side: "destination",
      complement: null,
      synonyms: [],
      rank: 1
    },
    {
      name: "proof",
      side: "destination",
      complement: null,
      synonyms: [],
      rank: 1
    },
    {
      name: "execution_failed",
      side: "destination",
      complement: null,
      synonyms: [],
      rank: 1
    },
    {
      name: "placement",
      side: "origin",
      complement: "fill",
      synonyms: [],
      rank: 0
    },
    {
      name: "fill",
      side: "destination",
      complement: null,
      synonyms: [],
      rank: 1
    },
    {
      name: "cancellation",
      side: "origin",
      complement: null,
      synonyms: [],
      rank: 0
    },
    {
      name: "acknowledgement",
      side: "origin",
      complement: null,
      synonyms: [],
      rank: 0
    },
    {
      name: "relock",
      side: "origin",
      complement: "destination",
      synonyms: [],
      rank: 0
    }
  ],
  unknown_role_rank: 2,
  default_complete_roles: [
    "source",
    "destination"
  ],
  open_ended: {
    saga_kinds: [
      "entity.flow",
      "flow.exchange_deposit",
      "market.lifecycle",
      "negrisk.market",
      "pool.lifecycle",
      "position.lifecycle",
      "spam.dust_campaign",
      "token.launch",
      "trade.token_position"
    ],
    saga_prefixes: [
      "trade."
    ],
    relationship_types: [
      "address_flow",
      "trade"
    ]
  }
};

// src/grouping/roles.ts
var ROLES = roles_generated_default.roles;
var BY_SPELLING = new Map(
  ROLES.flatMap((role) => [role.name, ...role.synonyms].map((spelling) => [spelling, role]))
);
var CANON_SOURCE = "source";
var CANON_DESTINATION = "destination";
var DEFAULT_COMPLETE_ROLES = roles_generated_default.default_complete_roles;
var ROLE_RANK = Object.fromEntries(
  [...BY_SPELLING].map(([spelling, role]) => [spelling, role.rank])
);
var UNKNOWN_ROLE_RANK = roles_generated_default.unknown_role_rank;
var ROLE_COMPLEMENT = Object.fromEntries(
  ROLES.filter((role) => role.complement).map((role) => [role.name, role.complement])
);
var OPEN_ENDED_SAGA_KINDS = new Set(roles_generated_default.open_ended.saga_kinds);
var OPEN_ENDED_SAGA_PREFIXES = roles_generated_default.open_ended.saga_prefixes;
var OPEN_ENDED_RELATIONSHIP_TYPES = new Set(
  roles_generated_default.open_ended.relationship_types
);
function canonRole(role) {
  const value = (role ?? "").trim().toLowerCase();
  return BY_SPELLING.get(value)?.name ?? value;
}
function roleSide(role) {
  return BY_SPELLING.get((role ?? "").trim().toLowerCase())?.side;
}
function roleRank(role) {
  return ROLE_RANK[(role ?? "").trim().toLowerCase()] ?? UNKNOWN_ROLE_RANK;
}
var ROLE_LABELS = {
  // A token's launch — `commons.grouping.token_launch`.
  token_deploy: "Token deployed",
  // A pool's lifecycle — `commons.grouping.pools`. Shared with the launch above.
  pool_open: "Pool created",
  pool_priced: "Pool priced",
  liquidity_add: "Liquidity added",
  liquidity_remove: "Liquidity removed",
  liquidity_move: "Liquidity changed",
  swap: "Traded",
  // A finding re-files its members under what the finding says they did.
  liquidity_pull: "Liquidity pulled",
  insider_buy: "Insider bought",
  insider_sell: "Insider sold",
  // A JIT bracket (`pool.jit_front_run`). Deliberately not the sandwich's
  // `frontrun`/`backrun` wording, which the backend keeps for `mev.sandwich`
  // alone: those legs move the price and take the difference out of the trade
  // between them, whereas these add real depth for its duration.
  jit_open: "Liquidity added just in time",
  jit_close: "Liquidity pulled straight back",
  // The trade the bracket was built around. NOT "victim": the added depth is real
  // while this trade executes, so it slips LESS than the pool alone would have
  // made it. The party that pays is the pool's passive LPs, diluted out of the
  // fee on this flow — and they are positions, so no group member is them.
  target_swap: "Trade the liquidity was placed around",
  // Custody — `commons.grouping.exchange_flow`. Direction is the whole meaning
  // of these two, and the prettifier flattens it: "Exchange Deposit" and
  // "Exchange Withdrawal" are noun phrases that leave the reader to work out
  // which way the money went and whose it was.
  exchange_deposit: "Sent to the exchange",
  exchange_withdrawal: "Received from the exchange",
  // A forwarding chain. Written as "as far back as we could see" rather than
  // "source", which is the claim the detector cannot make: the walk stops at
  // its depth bound or at the first hop nobody ingested, so this wallet may
  // well have a funder of its own.
  chain_origin: "Earliest hop we can see",
  chain_hop: "Passed the funds on",
  chain_deposit: "Reached the exchange"
};
function roleLabel(role) {
  const canon = canonRole(role);
  if (!canon) return "";
  const known = ROLE_LABELS[canon];
  if (known) return known;
  const words = canon.replace(/[._-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
function isOpenEndedGroup(sagaKind, relationshipType, openEnded) {
  if (openEnded === true) return true;
  const kind = (sagaKind ?? "").trim().toLowerCase();
  if (OPEN_ENDED_SAGA_KINDS.has(kind)) return true;
  if (OPEN_ENDED_SAGA_PREFIXES.some((prefix) => kind.startsWith(prefix))) return true;
  return OPEN_ENDED_RELATIONSHIP_TYPES.has((relationshipType ?? "").trim().toLowerCase());
}
function presentRoles(roles) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const role of roles) {
    const value = canonRole(role);
    if (value && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  }
  return out;
}
function expectedMissingRoles(rolesPresent, completeRoles) {
  const present = rolesPresent.filter((r) => r).map((r) => canonRole(r));
  const presentSet = new Set(present.filter((r) => r));
  let candidates = [];
  if (completeRoles && completeRoles.length > 0) {
    candidates = completeRoles.filter((r) => r).map((r) => canonRole(r));
  } else {
    for (const role of presentSet) {
      const complement = ROLE_COMPLEMENT[role];
      if (complement) candidates.push(complement);
    }
    if (candidates.length === 0 && presentSet.size === 0) candidates = [...DEFAULT_COMPLETE_ROLES];
  }
  const seen = /* @__PURE__ */ new Set();
  const expected = [];
  for (const role of [...present, ...candidates]) {
    if (role && !seen.has(role)) {
      seen.add(role);
      expected.push(role);
    }
  }
  return { expected, missing: expected.filter((role) => !presentSet.has(role)) };
}

// src/grouping/sagas.ts
var SAGA_LABELS = {
  "enterprise.payment": "Enterprise payment",
  "enterprise.payment_reversal": "Payment reversal",
  "bridge.ccip": "Chainlink CCIP",
  "bridge.cctp": "Circle CCTP",
  "bridge.wormhole": "Wormhole",
  // The rest of the bundled bridges, which fell through to the generic
  // prettify and so introduced themselves as "Bridge Layerzero V2" and
  // "Bridge Ibc" — the namespace leaked into the name and the protocol's own
  // capitalisation was lost. A card naming a protocol should spell it the way
  // the protocol does.
  //
  // Both LayerZero generations are listed and both say which: they are
  // different rails with different contracts and different chain numbering,
  // and a group card that called them both "LayerZero" would leave a reader
  // unable to tell which index to go and look in.
  "bridge.layerzero_v1": "LayerZero V1",
  "bridge.layerzero_v2": "LayerZero V2",
  "bridge.across_v3": "Across",
  "bridge.hyperlane": "Hyperlane",
  "bridge.mayan": "Mayan",
  // The one saga in this list whose key is not on the chain. A Relay fill is a
  // solver paying out of its own capital and identifies itself to nobody, so the
  // two legs are joined by the request id Relay's own API mints — carried as a
  // `CrossChainRef` with `off_chain` provenance. Nothing else here needs to know
  // that; the label is the only Relay-specific line in this package.
  "bridge.relay": "Relay",
  "bridge.thorchain": "THORChain",
  "bridge.chainflip": "Chainflip",
  "bridge.symbiosis": "Symbiosis",
  "bridge.hyperliquid": "Hyperliquid bridge",
  "bridge.hop": "Hop",
  "bridge.ibc": "IBC",
  "trade.token_position": "Trading position",
  "token.launch": "Token launch",
  "pool.lifecycle": "Pool lifecycle",
  // One liquidity position's whole story — its mint, every top-up, collection
  // and draw-down, and the hand-overs of the token that represents it — keyed
  // on the token id under its position manager.
  "position.lifecycle": "Liquidity position",
  // A prediction market's whole story, keyed on its `conditionId`. "Prediction
  // market" rather than "Conditional tokens" — the latter is the name of the
  // contract standard, and a reader looking at a group of bets on a question
  // should not have to know it.
  "market.lifecycle": "Prediction market",
  // The ELECTION, not one of its questions. A multi-outcome ("neg risk") market
  // is N binary conditions, each already titled "Prediction market" — naming
  // this one the same would put eleven identically-titled groups on a page and
  // leave the reader to work out that one of them is the other ten.
  "negrisk.market": "Multi-outcome market",
  "pool.rug": "Rug pull",
  "pool.jit_front_run": "JIT front-run",
  "mev.sandwich": "Sandwich",
  "launch.insider_buy": "Insider buy",
  "launch.insider_sell": "Insider sold after launch",
  // Custody. "Exchange transfers" rather than "Exchange deposits" because the
  // group holds both directions — the saga kind names the common case, the
  // group does not.
  "flow.exchange_deposit": "Exchange transfers",
  // One CoW order's placement and the settlement that filled it, keyed on the
  // derived `orderUid`. Prettified it read "Exchange Cowswap Order".
  "exchange.cowswap_order": "CoW Swap order",
  // One swap's Bitcoin legs, keyed on the 2-of-2 they pay and spend.
  "atomic_swap.xmr_btc": "XMR\u2194BTC atomic swap",
  // NOT "Peel chain", which is the technique's name among investigators and
  // means nothing to anybody else. What the finding says is that money moved
  // through a series of wallets and stopped somewhere it can no longer be
  // followed.
  //
  // "somewhere" rather than "an exchange" because the detector's terminus is a
  // CLASS now — an exchange, a custodial service, a bridge escrow, a mixer, a
  // sanctioned address. A static label naming one of them would be wrong for
  // the other four; `peelChainLabel` below narrows it when the group's payload
  // says which.
  "flow.peel_chain": "Funds forwarded through intermediaries",
  // NOT "Dust campaign", for the same reason the peel chain is not called one:
  // "dusting" is the technique's name among investigators and says nothing to
  // the person whose wallet is full of it. What the finding says is that one
  // address sprayed worthless transfers at a great many wallets.
  "spam.dust_campaign": "Bulk unsolicited transfers",
  "spam.address_poisoning": "Address poisoning",
  // The prediction-market findings, named for what the wallets DID and never
  // for what it may mean: "Informed bet", not "Insider trading" — a settlement
  // can show a wallet bought the winner cheaply before it was public, not how
  // it knew. Verbatim from `commons/grouping/naming.py`.
  "prediction.informed_entry": "Informed bet",
  "prediction.coordinated_entry": "Coordinated bets",
  "prediction.oracle_trade": "Authority traded own market",
  "prediction.improbable_record": "Improbable win record",
  "prediction.copy_trading": "Copy trading",
  "prediction.wash_trade": "Wash trading",
  // A declarative risk pattern matched over one actor's findings.
  "actor.risk_pattern": "Risk pattern",
  approve_spend: "Approve / spend",
  "userop.retry": "User-op retry",
  userop_retry: "User-op retry",
  "bridge.usdt0": "USDT0 transfer",
  "bridge.stargate_v2": "Stargate V2 transfer",
  "bridge.near_intents": "NEAR Intents transfer",
  "staking.lido_withdrawal": "Lido withdrawal",
  // Every transaction touching one catalog entity — a set of wallets someone
  // labelled. The served group name is "Entity activity · <entity name>"
  // (`commons.grouping.naming`), and a commons test fails if this map and that
  // one spell a family differently.
  "entity.flow": "Entity activity"
};
var FINDING_KIND_SAGAS = {
  mev_sandwich: "mev.sandwich",
  liquidity_front_run: "pool.jit_front_run",
  liquidity_rug: "pool.rug",
  token_insider_buy: "launch.insider_buy",
  token_insider_sell: "launch.insider_sell",
  peel_chain: "flow.peel_chain",
  dust_campaign: "spam.dust_campaign",
  address_poisoning: "spam.address_poisoning",
  risk_pattern: "actor.risk_pattern",
  prediction_informed_entry: "prediction.informed_entry",
  prediction_coordinated_entry: "prediction.coordinated_entry",
  prediction_oracle_trade: "prediction.oracle_trade",
  prediction_improbable_record: "prediction.improbable_record",
  prediction_copy_trading: "prediction.copy_trading",
  prediction_wash_trade: "prediction.wash_trade"
};
function findingKindLabel(kind) {
  const saga = FINDING_KIND_SAGAS[kind];
  return saga && SAGA_LABELS[saga] || kind;
}
var GROUP_RELATIONSHIP_LABELS = {
  userop_retry: "User-op retry",
  cross_chain_message: "Cross-chain",
  intent_fulfillment: "Cross-chain",
  approve_spend: "Approve / spend",
  "userop.retry": "Retry",
  entity_flow: "Entity flow",
  trade: "Position",
  pool_lifecycle: "Pool activity",
  token_launch: "Token launch",
  market_lifecycle: "Market activity",
  position_lifecycle: "Position activity",
  exchange_deposit: "Exchange flow",
  // One escrow's steps: an atomic swap's cancel and its refund or punish.
  escrow_lifecycle: "Escrow",
  address_flow: "Address activity",
  // The finding categories. They were bare strings on the detector's writer
  // until the enum learned them, so every one of them landed on the "Linked"
  // fallback below — which reads as "we found a relationship but can't say
  // what", about the groups that exist precisely because something was named.
  jit_liquidity: "JIT liquidity",
  mev_sandwich: "Sandwich",
  liquidity_rug: "Rug pull",
  insider_buy: "Insider buy",
  insider_sell: "Insider sold after launch",
  peel_chain: "Forwarded through wallets",
  dust_campaign: "Bulk transfers",
  address_poisoning: "Address poisoning",
  informed_entry: "Informed bet",
  coordinated_entry: "Coordinated bets",
  oracle_trade: "Authority trade",
  improbable_record: "Improbable record",
  copy_trading: "Copy trading",
  wash_trade: "Wash trading",
  risk_pattern: "Risk pattern",
  custom: "Linked"
};
function groupRelationshipLabel(relationshipType, sagaKind) {
  const key = (relationshipType ?? "").trim().toLowerCase();
  const saga = (sagaKind ?? "").trim().toLowerCase();
  if (key === "intent_fulfillment" && saga && !saga.startsWith("bridge.")) return "Intent";
  return GROUP_RELATIONSHIP_LABELS[key] ?? "Linked";
}
var FINDING_SAGA_KINDS = /* @__PURE__ */ new Set([
  "pool.rug",
  "pool.jit_front_run",
  "mev.sandwich",
  "launch.insider_buy",
  "launch.insider_sell",
  // The `flow.` namespace splits the same way `pool.` does: a wallet's
  // transfers to an exchange are ordinary activity, while funds arriving there
  // through a run of intermediate wallets is a detector's claim about them.
  "flow.peel_chain",
  // A settlement's findings: each card enumerates the few fills (and the
  // resolution) that make the case.
  "prediction.informed_entry",
  "prediction.coordinated_entry",
  "prediction.oracle_trade",
  "prediction.improbable_record",
  "prediction.copy_trading",
  "prediction.wash_trade",
  // The transactions a matched risk pattern cites (a capped few).
  "actor.risk_pattern",
  "spam.address_poisoning"
  // NOTE: `spam.dust_campaign` is deliberately NOT here, though a detector is
  // what recognises it and `tx_grouping.config.policy.FINDING_SAGA_KINDS` does
  // list it. That set decides SCOPE; this one decides how the group renders,
  // and a finding card enumerates the legs that make the case — three for a
  // sandwich, four for a peel chain. A campaign has forty thousand. It is in
  // ACTIVITY_SAGA_KINDS, matching `commons/grouping/shapes.py`.
]);
function peelChainLabel(terminusClass) {
  const phrase = terminusPhrase(terminusClass);
  if (phrase) return `Funds forwarded to ${phrase}`;
  return SAGA_LABELS["flow.peel_chain"] ?? "Funds forwarded through intermediaries";
}
function isFindingSaga(kind) {
  return FINDING_SAGA_KINDS.has((kind ?? "").trim().toLowerCase());
}
var POSITION_SAGA_KINDS = /* @__PURE__ */ new Set(["trade.token_position"]);
var ACTIVITY_SAGA_KINDS = /* @__PURE__ */ new Set([
  "pool.lifecycle",
  "token.launch",
  // A prediction market's history: its creation, every position opened and
  // closed on it, its resolution and the payouts after. Open-ended over one
  // subject exactly like a pool, and capped the same way.
  "market.lifecycle",
  // A liquidity position's history. Open-ended over one subject like its
  // pool's: a compounding vault touches its one position every few minutes.
  "position.lifecycle",
  // The multi-outcome market above those, open-ended for the same reason and
  // over more legs: an election holds every one of its questions at once.
  "negrisk.market",
  // One address's bulk dusting — the shape this set exists for, taken to its
  // extreme. A campaign's members number in the tens of thousands, so the only
  // useful card summarises and links out. See the note in FINDING_SAGA_KINDS on
  // why a detector-recognised group is nonetheless not a finding CARD.
  "spam.dust_campaign"
]);
var CUSTODY_SAGA_KINDS = /* @__PURE__ */ new Set([
  "flow.exchange_deposit"
]);
function sagaKindsByShape() {
  const known = /* @__PURE__ */ new Set([
    ...Object.keys(SAGA_LABELS),
    ...FINDING_SAGA_KINDS,
    ...POSITION_SAGA_KINDS,
    ...ACTIVITY_SAGA_KINDS,
    ...CUSTODY_SAGA_KINDS
  ]);
  const order = [
    "finding",
    "crossChain",
    "activity",
    "position",
    "custody",
    "linked"
  ];
  const buckets = /* @__PURE__ */ new Map();
  for (const kind of known) {
    const category = groupCardCategory(kind, null);
    const list = buckets.get(category) ?? [];
    list.push(kind);
    buckets.set(category, list);
  }
  return order.map((category) => ({
    category,
    title: GROUP_CARD_TITLES[category],
    kinds: (buckets.get(category) ?? []).sort(
      (a, b) => sagaLabel(a).localeCompare(sagaLabel(b))
    )
  })).filter((bucket) => bucket.kinds.length > 0);
}
var GROUP_CARD_TITLES = {
  crossChain: "Cross-chain",
  finding: "Findings",
  position: "Trading position",
  // Addresses too: an authored address-flow group (a wallet set's transfers) is
  // an activity stream, and a heading of pools and tokens misfiled it.
  activity: "Pool, token & address activity",
  custody: "Exchange transfers",
  linked: "Related transactions"
};
var GROUP_SHAPE_LABELS = {
  crossChain: "Cross-chain",
  finding: "Finding",
  position: "Position",
  activity: "Activity",
  custody: "Exchange",
  linked: "Linked"
};
var GROUP_SHAPE_MEANING = {
  crossChain: "Legs of one transfer on different chains, linked by a shared message id.",
  finding: "A detector recognised a pattern here \u2014 membership in this group is itself the finding.",
  position: "One wallet's buys and sells of one token.",
  activity: "Everything that has happened to one pool or token launch.",
  custody: "One wallet's transfers to and from a custodial venue.",
  linked: "Related transactions the engine linked without a more specific shape."
};
function groupShapeLabel(sagaKind, relationshipType) {
  return GROUP_SHAPE_LABELS[groupCardCategory(sagaKind, relationshipType)];
}
function groupShapeMeaning(sagaKind, relationshipType) {
  return GROUP_SHAPE_MEANING[groupCardCategory(sagaKind, relationshipType)];
}
var GROUP_STATE_MEANING = {
  open: "Watching for further legs.",
  partial: "Some legs have arrived; still watching for the rest.",
  completed: "Every expected leg arrived.",
  timed_out: "Stopped waiting for the missing leg. That is a guess about a leg that never arrived, not a verdict \u2014 a reprocess can still reopen this group if the leg turns up.",
  cancelled: "Deliberately closed. This does not reopen.",
  reorged: "A member transaction left the canonical chain. This does not reopen."
};
function groupStateMeaning(state) {
  return GROUP_STATE_MEANING[(state ?? "").trim().toLowerCase()] ?? "";
}
function isWatchingGroupState(state) {
  const value = (state ?? "").trim().toLowerCase() || "open";
  return value === "open" || value === "partial";
}
function isReconsiderableState(state) {
  const value = (state ?? "").trim().toLowerCase();
  return value === "open" || value === "partial" || value === "timed_out";
}
function isReopenableState(state) {
  return (state ?? "").trim().toLowerCase() === "timed_out";
}
function groupStateLabel(state) {
  const value = (state ?? "").trim().toLowerCase() || "open";
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ");
}
function groupCardCategory(sagaKind, relationshipType) {
  const kind = (sagaKind ?? "").trim().toLowerCase();
  if (kind) {
    if (FINDING_SAGA_KINDS.has(kind)) return "finding";
    if (POSITION_SAGA_KINDS.has(kind)) return "position";
    if (ACTIVITY_SAGA_KINDS.has(kind)) return "activity";
    if (CUSTODY_SAGA_KINDS.has(kind)) return "custody";
    if (kind.startsWith("bridge.")) return "crossChain";
    if (kind.startsWith("trade.")) return "position";
  }
  const relationship = (relationshipType ?? "").trim().toLowerCase();
  if (relationship === "cross_chain_message" || relationship === "intent_fulfillment") {
    return "crossChain";
  }
  if (relationship === "pool_lifecycle" || relationship === "token_launch" || relationship === "market_lifecycle" || relationship === "position_lifecycle" || relationship === "dust_campaign" || // Every transaction between addresses a rule named: a stream about one
  // subject, like a pool's history. Mirrors `_RELATIONSHIP_SHAPES`.
  relationship === "address_flow") {
    return "activity";
  }
  if (relationship === "trade") return "position";
  if (relationship === "exchange_deposit") return "custody";
  if (relationship === "address_poisoning" || relationship === "jit_liquidity" || relationship === "mev_sandwich" || relationship === "liquidity_rug" || relationship === "insider_buy" || relationship === "insider_sell" || relationship === "peel_chain") {
    return "finding";
  }
  return "linked";
}
function groupTransactionCount(group) {
  const txs = group.transaction_count;
  if (typeof txs === "number" && txs > 0) return txs;
  const members = group.members?.length;
  if (typeof members === "number" && members > 0) return members;
  const legs = group.member_count;
  return typeof legs === "number" && legs > 0 ? legs : 0;
}
function isSoleMemberActivity(group) {
  if (!isOpenEndedGroup(group.saga_kind, group.relationship_type, group.open_ended)) {
    return false;
  }
  return groupTransactionCount(group) <= 1;
}
function compareGroupCards(a, b) {
  const soleA = isSoleMemberActivity(a);
  const soleB = isSoleMemberActivity(b);
  if (soleA !== soleB) return soleA ? 1 : -1;
  const sizeDelta = groupTransactionCount(b) - groupTransactionCount(a);
  if (sizeDelta !== 0) return sizeDelta;
  return (a.saga_kind ?? "").localeCompare(b.saga_kind ?? "");
}
function prettifyToken(value) {
  const words = value.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  return words.length === 0 ? value : words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function sagaLabel(kind) {
  if (!kind) return "Cross-chain";
  const listed = SAGA_LABELS[kind];
  if (listed) return listed;
  if (kind.startsWith("bridge.")) {
    const named = protocolDisplayName(kind.slice("bridge.".length));
    if (named) return named;
  }
  return prettifyToken(kind);
}
var SAGA_ID_LABELS = {
  message_id: "Message ID",
  vaa_id: "VAA",
  sequence: "Sequence",
  nonce: "Nonce",
  // The tuple keys. A prettify would read "Packet Path" / "Deposit Key", which
  // is close enough to leave alone — but "Pathway" is what LayerZero calls it
  // and "Packet origin" is what the V2 spec's own comment calls the tuple, so
  // spelling them here costs one line and stops the surface inventing a name
  // for something the protocol already named.
  packet_path: "Packet pathway",
  packet_origin: "Packet origin",
  deposit_key: "Deposit",
  transfer_id: "Transfer ID",
  packet_id: "Packet",
  // The exchange-flow key names the CUSTOMER's wallet — one group per wallet
  // per venue (`EXCHANGE_FLOW_ID_LABEL`). Prettified it read "Exchange Wallet",
  // which is the venue's hot wallet to anybody reading a forensics page: the
  // one address on it the saga is NOT about.
  exchange_wallet: "Wallet"
};
function shortCorrelationId(value) {
  const text = value.trim();
  const parts = text.split(":");
  if (parts.length === 1) {
    return text.length > 20 ? truncateMiddle(text, { head: hexPrefixOf(text) ? 10 : 12, tail: 4 }) : text;
  }
  return parts.map((part) => {
    if (/^0x[0-9a-f]+$/i.test(part)) {
      const body = part.slice(2);
      return shortenAddress(body.length === 64 && /^0{24}/.test(body) ? `0x${body.slice(24)}` : part);
    }
    return part.length > 20 ? truncateMiddle(part, { head: 8, tail: 4 }) : part;
  }).join(":");
}
function correlationIdText(value, idLabel, fallback = shortCorrelationId) {
  const raw = value.trim();
  const key = (idLabel ?? "").trim().toLowerCase();
  if (key === "exchange_wallet") {
    const parts = raw.split(":");
    if (parts.length >= 4) {
      const wallet = parts[parts.length - 1];
      const chain = `${parts[0]}:${parts[1]}`;
      const chainName = chainDisplayName(chain);
      const short = shortenAddress(wallet);
      return {
        text: chainName && chainName !== chain ? `${short} on ${chainName}` : short,
        title: raw
      };
    }
  }
  return { text: fallback(raw), title: raw };
}
function sagaIdLabel(idLabel) {
  const key = (idLabel ?? "").trim().toLowerCase();
  if (!key) return "Correlation ID";
  return SAGA_ID_LABELS[key] ?? prettifyToken(key);
}
function titleRole(role) {
  return prettifyToken(role);
}
function linkageValue(value) {
  const text = (value ?? "").trim();
  return /^(?:0x[0-9a-f]+|[0-9a-f]{64})$/i.test(text) ? text.toLowerCase() : text;
}
function sagaKeyOf(ref) {
  const kind = (ref.sagaKind ?? "").trim().toLowerCase();
  const value = linkageValue(ref.value);
  if (!kind || !value) return null;
  return `${kind}:${value}`;
}
function compareSagas(a, b) {
  if (a.awaiting !== b.awaiting) return a.awaiting ? -1 : 1;
  if (a.legs.length !== b.legs.length) return b.legs.length - a.legs.length;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}
function currentIdentityGroups(groups, refs) {
  const replacements = refs.filter((ref) => ref.supersedes?.length && groups.some((group) => group.saga_kind === ref.sagaKind && group.id_label === ref.idLabel && linkageValue(group.id_value) === linkageValue(ref.value)));
  return groups.filter((group) => !replacements.some((ref) => group.saga_kind === ref.sagaKind && ref.supersedes.some((prior) => group.id_label === prior.idLabel && linkageValue(group.id_value) === linkageValue(prior.value) && (group.correlation_scope ?? "") === (prior.correlationScope ?? ""))));
}
function refsFromGroups(groups) {
  const out = [];
  for (const group of groups) {
    const sagaKind = group.saga_kind?.trim();
    if (!sagaKind || group.open_ended) continue;
    if (groupCardCategory(sagaKind, group.relationship_type) !== "crossChain") continue;
    const value = (group.id_value ?? "").trim() || group.group_id;
    out.push({
      value: linkageValue(value),
      sagaKind,
      role: group.role ?? void 0,
      idLabel: group.id_label ?? void 0,
      relationshipType: group.relationship_type ?? void 0,
      linkageModel: group.linkage_model ?? void 0,
      confidence: group.confidence ?? void 0,
      remoteChainId: group.remote_chain_id ?? void 0,
      completeRoles: group.expected_roles ? [...group.expected_roles] : void 0,
      source: "grouping_engine"
    });
  }
  return out;
}
function sagasFromRefs(entries, rowKey) {
  const buckets = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    const key = rowKey(entry.row);
    for (const ref of entry.refs) {
      const sagaKey = sagaKeyOf(ref);
      if (!sagaKey) continue;
      let bucket = buckets.get(sagaKey);
      if (!bucket) {
        const sagaKind = (ref.sagaKind ?? "").trim().toLowerCase();
        bucket = {
          seen: /* @__PURE__ */ new Set(),
          saga: {
            key: sagaKey,
            sagaKind,
            value: linkageValue(ref.value),
            protocolId: ref.protocolId,
            idLabel: ref.idLabel,
            linkageType: ref.linkageType,
            linkageModel: ref.linkageModel,
            relationshipType: ref.relationshipType,
            legs: [],
            rolesPresent: [],
            expectedRoles: [],
            missingRoles: [],
            awaiting: false,
            complete: false,
            openEnded: isOpenEndedGroup(sagaKind, ref.relationshipType)
          }
        };
        buckets.set(sagaKey, bucket);
      }
      if (!bucket.seen.has(key)) {
        bucket.seen.add(key);
        const role = canonRole(ref.role);
        bucket.saga.legs.push({
          row: entry.row,
          role,
          rawRole: ref.role && ref.role !== role ? ref.role : void 0,
          ref
        });
      }
    }
  }
  for (const { saga } of buckets.values()) {
    saga.legs.sort((a, b) => roleRank(a.role) - roleRank(b.role));
    const declared = saga.legs.map((leg) => leg.ref.completeRoles).find((r) => r && r.length > 0);
    const roles = saga.legs.map((leg) => leg.role);
    const { expected, missing } = expectedMissingRoles(roles, declared);
    saga.rolesPresent = [...new Set(roles.filter(Boolean))];
    saga.expectedRoles = expected;
    saga.missingRoles = saga.openEnded ? [] : missing;
    saga.complete = saga.missingRoles.length === 0;
    saga.awaiting = !saga.openEnded && !saga.complete;
  }
  const sagas = [...buckets.values()].map((b) => b.saga).sort(compareSagas);
  return { sagas, byRow: rowStatesFromSagas(sagas, rowKey) };
}
function hashesOf(group) {
  const out = /* @__PURE__ */ new Set();
  for (const member of group.members ?? []) {
    const hash = normalizeTxHash(member.tx_hash ?? "", member.chain_id);
    if (hash) out.add(hash);
  }
  return out;
}
function reconcileSagas(sagas, groupsFor, txHashOf) {
  if (sagas.length === 0) return [];
  const out = sagas.map((saga) => {
    const legHashes = new Set(
      saga.legs.map((leg) => normalizeTxHash(txHashOf(leg.row) ?? "")).filter(Boolean)
    );
    let best;
    let bestOverlap = 0;
    const seen = /* @__PURE__ */ new Set();
    for (const leg of saga.legs) {
      for (const group of groupsFor(leg.row)) {
        if (seen.has(group.group_id)) continue;
        seen.add(group.group_id);
        if (saga.sagaKind && group.saga_kind && group.saga_kind !== saga.sagaKind) continue;
        const identity = linkageValue(group.id_value);
        if (identity && identity !== saga.value && group.group_id !== saga.value) continue;
        if (group.id_label && saga.idLabel && group.id_label !== saga.idLabel) continue;
        let overlap = 0;
        for (const hash of hashesOf(group)) if (legHashes.has(hash)) overlap += 1;
        const candidateHashes = hashesOf(group);
        const priorHashes = best ? hashesOf(best) : /* @__PURE__ */ new Set();
        const extendsSameMessage = !!best && !!identity && identity === linkageValue(best.id_value) && candidateHashes.size > priorHashes.size && [...priorHashes].every((hash) => candidateHashes.has(hash));
        if (overlap > bestOverlap || overlap > 0 && overlap === bestOverlap && extendsSameMessage) {
          bestOverlap = overlap;
          best = group;
        }
      }
    }
    if (!best) return saga;
    const closed = best.state?.toLowerCase() === "cancelled";
    const memberRole = (member) => closed && (member.role === "cancellation" || member.roles?.includes("cancellation")) ? "cancellation" : member.role ?? "";
    const closingHashes = new Set((best.members ?? []).filter((member) => memberRole(member) === "cancellation").map((member) => normalizeTxHash(member.tx_hash ?? "", member.chain_id)).filter(Boolean));
    const legs = closed ? saga.legs.map((leg) => closingHashes.has(normalizeTxHash(txHashOf(leg.row) ?? "")) ? { ...leg, role: "cancellation" } : leg) : saga.legs;
    const missing = [...best.missing_roles ?? []];
    const expected = best.expected_roles?.length ? [...best.expected_roles] : saga.expectedRoles;
    const elsewhere = [];
    const listed = new Set(legHashes);
    for (const member of best.members ?? []) {
      const hash = (member.tx_hash ?? "").trim();
      const normalized = normalizeTxHash(hash, member.chain_id);
      if (!hash || listed.has(normalized)) continue;
      listed.add(normalized);
      elsewhere.push({ role: memberRole(member), chainId: member.chain_id ?? null, txHash: hash, txId: member.tx_id ?? null });
    }
    elsewhere.sort((a, b) => roleRank(a.role) - roleRank(b.role));
    const openEnded = best.open_ended ?? saga.openEnded;
    return {
      ...saga,
      legs,
      rolesPresent: [...new Set(legs.map((leg) => leg.role).filter(Boolean))],
      expectedRoles: expected,
      missingRoles: openEnded ? [] : missing,
      awaiting: openEnded || closed ? false : best.awaiting ?? missing.length > 0,
      complete: !closed && (openEnded || missing.length === 0),
      closed,
      openEnded,
      confirmed: true,
      groupId: best.group_id,
      groupState: best.state ?? void 0,
      memberCount: best.member_count ?? void 0,
      elsewhere
    };
  });
  return out.sort(compareSagas);
}
function rowStatesFromSagas(sagas, rowKey) {
  const byRow = /* @__PURE__ */ new Map();
  for (const saga of sagas) {
    for (const leg of saga.legs) {
      const key = rowKey(leg.row);
      const list = byRow.get(key);
      if (list) list.push(saga);
      else byRow.set(key, [saga]);
    }
  }
  const out = /* @__PURE__ */ new Map();
  for (const [key, rowSagas] of byRow) {
    const roles = new Set(
      rowSagas.flatMap((s) => s.legs.filter((l) => rowKey(l.row) === key).map((l) => l.role)).filter(Boolean)
    );
    out.set(key, {
      sagas: rowSagas,
      complete: rowSagas.filter((s) => s.complete).length,
      closed: rowSagas.filter((s) => s.closed).length,
      total: rowSagas.length,
      missingRoles: [...new Set(rowSagas.filter((s) => !s.closed).flatMap((s) => s.missingRoles))],
      awaiting: rowSagas.some((s) => s.awaiting),
      role: roles.size === 1 ? [...roles][0] : void 0,
      sagaKind: rowSagas[0]?.sagaKind,
      protocolId: rowSagas[0]?.protocolId,
      confirmed: rowSagas.every((s) => s.confirmed) ? true : void 0
    });
  }
  return out;
}

export { ACTIVITY_SAGA_KINDS, CANON_DESTINATION, CANON_SOURCE, CUSTODY_SAGA_KINDS, DEFAULT_COMPLETE_ROLES, FINDING_KIND_SAGAS, FINDING_SAGA_KINDS, GROUP_CARD_TITLES, GROUP_RELATIONSHIP_LABELS, GROUP_SHAPE_LABELS, GROUP_SHAPE_MEANING, GROUP_STATE_MEANING, OPEN_ENDED_RELATIONSHIP_TYPES, OPEN_ENDED_SAGA_KINDS, OPEN_ENDED_SAGA_PREFIXES, POSITION_SAGA_KINDS, ROLE_COMPLEMENT, ROLE_LABELS, ROLE_RANK, SAGA_ID_LABELS, SAGA_LABELS, UNKNOWN_ROLE_RANK, canonRole, compareGroupCards, compareSagas, correlationIdText, currentIdentityGroups, expectedMissingRoles, findingKindLabel, groupCardCategory, groupRelationshipLabel, groupShapeLabel, groupShapeMeaning, groupStateLabel, groupStateMeaning, groupTransactionCount, isFindingSaga, isOpenEndedGroup, isReconsiderableState, isReopenableState, isSoleMemberActivity, isWatchingGroupState, peelChainLabel, presentRoles, prettifyToken, reconcileSagas, refsFromGroups, roleLabel, roleRank, roleSide, rowStatesFromSagas, sagaIdLabel, sagaKindsByShape, sagaLabel, sagasFromRefs, shortCorrelationId, titleRole };
//# sourceMappingURL=chunk-5G23SR6G.js.map
//# sourceMappingURL=chunk-5G23SR6G.js.map